import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, MailCheck, MailX, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [params] = useSearchParams();
  const token = params.get("token");
  const [status, setStatus] = useState("verifying");
  const [error, setError] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  // React 19 StrictMode fires effects twice in dev, which would consume the
  // single-use token on the first call and then surface a misleading
  // "Invalid or already-used" error on the second. Guard with a ref +
  // sessionStorage so the verify call only ever happens once per token.
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setError("This link is missing its verification token.");
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    const cacheKey = `ae:verify-email:${token}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached === "ok") {
      setStatus("ok");
      refreshUser().finally(() => {
        setTimeout(() => navigate("/", { replace: true }), 1500);
      });
      return;
    }
    api.post("/auth/verify-email", { token })
      .then(async () => {
        sessionStorage.setItem(cacheKey, "ok");
        await refreshUser();
        setStatus("ok");
        setTimeout(() => navigate("/", { replace: true }), 1500);
      })
      .catch((err) => {
        // If we previously succeeded on this token in this tab, ignore the
        // second-call "already-used" error and stay on the success state.
        if (sessionStorage.getItem(cacheKey) === "ok") return;
        setStatus("error");
        setError(formatApiError(err.response?.data?.detail) || "Verification failed");
      });
  }, [token, navigate, refreshUser]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail.includes("@")) {
      setError("Please enter a valid email");
      return;
    }
    setResending(true);
    setError("");
    try {
      await api.post("/auth/resend-verification", { email: resendEmail });
      setResent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Couldn't re-send");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center" data-testid="verify-email-card">
        {status === "verifying" && (
          <>
            <Loader2 className="w-10 h-10 text-slate-400 animate-spin mx-auto" />
            <h2 className="font-heading text-2xl font-semibold mt-4 text-slate-900">Verifying…</h2>
            <p className="text-sm text-slate-500 mt-2">Hang tight, we're activating your account.</p>
          </>
        )}
        {status === "ok" && (
          <>
            <div className="w-14 h-14 rounded-full bg-emerald-500 grid place-items-center mx-auto">
              <MailCheck className="w-7 h-7 text-slate-900" />
            </div>
            <h2 className="font-heading text-2xl font-semibold mt-4 text-slate-900" data-testid="verify-email-success">Email verified</h2>
            <p className="text-sm text-slate-500 mt-2">Redirecting you to your dashboard…</p>
          </>
        )}
        {status === "error" && (
          <>
            <div className="w-14 h-14 rounded-full bg-rose-50 grid place-items-center mx-auto">
              <MailX className="w-7 h-7 text-rose-500" />
            </div>
            <h2 className="font-heading text-2xl font-semibold mt-4 text-slate-900" data-testid="verify-email-error">Verification failed</h2>
            <p className="text-sm text-slate-500 mt-2 text-left">{error}</p>

            {resent ? (
              <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-sm text-left" data-testid="verify-email-resent">
                <p className="text-sm font-medium text-emerald-800">A new verification link is on the way</p>
                <p className="text-xs text-emerald-700 mt-1">
                  Check <strong>{resendEmail}</strong> in a minute or two. The new link is valid for 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleResend} className="mt-6 text-left" data-testid="verify-email-resend-form">
                <Label htmlFor="resend-email" className="label-overline">Send me a new link</Label>
                <Input
                  id="resend-email"
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="The email you registered with"
                  className="mt-2"
                  required
                  data-testid="verify-email-resend-input"
                />
                <Button
                  type="submit"
                  disabled={resending}
                  className="w-full mt-3 bg-slate-900 hover:bg-slate-800 text-white"
                  data-testid="verify-email-resend-submit"
                >
                  {resending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Re-send verification email
                </Button>
              </form>
            )}

            <div className="mt-6">
              <Link to="/login" className="text-sm text-slate-500 hover:text-slate-700 underline" data-testid="verify-email-go-login">Back to sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
